/**
 * Quick Actions prompt library.
 *
 * Single source of truth for the categorized, ready-to-run prompts shown in
 * the sidebar "Quick Actions" panel and the AI Assistant welcome screen.
 *
 * Every prompt here is regression-tested with its exact wording in
 * tests/test_ai_service.py — keep the two files in sync when editing.
 */

export interface PromptCategory {
  key: string;
  label: string;
  /** SVG path (24x24, stroke) rendered next to the category heading. */
  icon: string;
  prompts: string[];
}

export const PROMPT_CATEGORIES: PromptCategory[] = [
  {
    key: 'accounts',
    label: 'Accounts & Net Worth',
    icon: 'M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11m16-11v11M8 14v3m4-3v3m4-3v3',
    prompts: [
      "What's my net worth?",
      'Show all my account balances',
      'How much do I owe on credit cards?',
    ],
  },
  {
    key: 'spending',
    label: 'Spending',
    icon: 'M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z',
    prompts: [
      'What did I spend this month?',
      'Show my transactions from last week',
      'Show my top spending categories for the last 30 days',
      'How much have I spent on coffee lately?',
      'Show all my Amazon purchases from the last 30 days',
      'How much did I spend on groceries last month?',
      'How much do I spend on streaming subscriptions?',
    ],
  },
  {
    key: 'budgets',
    label: 'Budgets',
    icon: 'M11 3.055A9.001 9.001 0 1020.945 13H11V3.055zM20.488 9H15V3.512A9.025 9.025 0 0120.488 9z',
    prompts: [
      'How am I doing against my budgets?',
      'Am I over budget in any category this month?',
      'Suggest a budget plan based on my spending',
      'Set a $500 monthly budget for groceries',
      'Am I following the 50/30/20 rule?',
      'What categories can I set a budget for?',
      'Remove one of my budgets',
    ],
  },
  {
    key: 'goals',
    label: 'Savings Goals',
    icon: 'M12 21a9 9 0 100-18 9 9 0 000 18zm0-4a5 5 0 100-10 5 5 0 000 10zm0-4a1 1 0 100-2 1 1 0 000 2z',
    prompts: [
      'Help me set up a new savings goal',
      'How are my goals progressing?',
      'Add money to one of my goals',
      'Can I afford to save $500 a month?',
      'Cancel one of my savings goals',
    ],
  },
  {
    key: 'investing',
    label: 'Investment Options',
    icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
    prompts: [
      'What could I do with my spare cash?',
      'What are current investment rates?',
      'What could $5,000 become in 10 years?',
      'What if I invest $1,000 plus $200 a month for 10 years?',
      'How do Treasury bills compare to the S&P 500?',
    ],
  },
  {
    key: 'trends',
    label: 'Trends & Charts',
    icon: 'M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z',
    prompts: [
      'Am I spending more than last month?',
      'Chart my spending by category this month',
      'Chart my net worth over time',
      'Show my cash flow — where does my money go?',
      'How has my grocery spending changed over 6 months?',
      'Chart my Starbucks spending over the last 6 months',
    ],
  },
  {
    key: 'health',
    label: 'Health & Safety',
    icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    prompts: [
      'How am I doing financially?',
      'Where can I cut back to save more money?',
      'Any suspicious or unusual transactions recently?',
      'Do I have any forgotten subscriptions?',
    ],
  },
];

/**
 * Curated picks shown on the AI Assistant welcome screen — keep this list short
 * so the empty state stays compact.
 */
export const WELCOME_SCREEN_PROMPTS: string[] = [
  "What's my net worth?",
  'Show my top spending categories for the last 30 days',
  'Suggest a budget plan based on my spending',
  'Help me set up a new savings goal',
  'Show my cash flow — where does my money go?',
  'How am I doing financially?',
  'Any suspicious or unusual transactions recently?',
  'Where can I cut back to save more money?',
];
